import { Alert, App, Button, Checkbox, Col, Divider, Form, Input, Row, Select, Spin, Switch, Tag } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SelectorSelect from 'src/common/components/business/selector-select';
import { PermissionsConstants, ScopeType, Selector, SelectorValue } from 'src/common/types';
import {
  generateSelectorOdataFilter,
  getKeyValuePairs,
  getRolePermissionSelectors,
  getScopeTypeRangeBySelector,
  isScopeTypeSelectorOk,
  toObject,
} from 'src/common/utils';
import { useIdentity } from 'src/core/services/authentication';
import { Query } from 'src/core/stores/data-store';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { container } from 'src/inversify.config';
import { PermissionSummary, PermissionSummaryStore } from 'src/stores/permissions-store';
import { PartnerAdminRoleName, RoleItemStore, RoleSummaryStore, SystemAdminRoleName } from 'src/stores/roles-store';

import { theme } from 'antd';
import 'src/assets/cards-detail-view-layout.less';
import OrganizationsSelect from 'src/common/components/business/organization-select';
import { DefaultRoleItemStore, DefaultRoleSummaryStore } from 'src/stores/default-roles-store';
import { guid } from 'odata-query';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { RuleObject } from 'antd/es/form';
import { curateString } from 'src/core/utils/object';

const { useToken } = theme;

const perms = [PermissionsConstants.ManageGlobalLicenses];

type RoleInformationProps = {
  header: string;
  routes: { path: string; breadcrumbName: string }[];
  maxSelector: Selector;
  mode: 'view' | 'edit' | 'new';
  previousRoute: string;
  canBeAssignedByDefaultToScopeCreator?: boolean;
  canBePartnersOnly?: boolean;
  canSelectOrganization?: boolean;
  isDefaultRole?: boolean;
};

const RoleInformationPage: FC<RoleInformationProps> = ({
  header,
  routes,
  maxSelector,
  mode,
  canBeAssignedByDefaultToScopeCreator,
  canBePartnersOnly,
  isDefaultRole,
  canSelectOrganization,
  previousRoute,
}) => {
  const { t } = useTranslation();

  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const roleId = params.id!;
  let roleItem = location.state;
  const identity = useIdentity();
  const currentOrg = identity.profile['tenant_id'];
  const currentOrgName = identity.profile['tenant_name'];

  const [permissionSelection, setPermissionSelection] = useState<{ [key: string]: { permission: PermissionSummary; allowed: boolean } }>({});
  const [roleName, setRoleName] = useState<string>();
  const [roleSelector, setRoleSelector] = useState<Selector>();
  const [addToScopeTypeCreator, setAddToScopeTypeCreator] = useState<ScopeType>();
  const [partnersOnly, setPartnersOnly] = useState<boolean>(false);
  const [form] = Form.useForm<{
    roleName: string;
    selector: Selector;
    partnersOnly: boolean;
    addToScopeTypeCreator?: ScopeType;
    organization?: string;
  }>();
  const [roleOrg, setRoleOrg] = useState<{ label: string; value: string }>();

  const roleStore = isDefaultRole ? container.get(DefaultRoleItemStore) : container.get(RoleItemStore);
  const roleState = roleStore.state;

  const permissionsStore = container.get(PermissionSummaryStore);
  const permissionsState = permissionsStore.state;
  const { notification } = App.useApp();
  const { token } = useToken();
  const bgColor = token.colorBgContainer;
  const roleSummaryStore = isDefaultRole ? container.get(DefaultRoleSummaryStore) : container.get(RoleSummaryStore);
  const roleSummaryState = roleSummaryStore.state;

  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, perms);
  const hasGlobalLicensemanagerPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses);

  setHeader(header);
  setRoutes(routes);

  const permissionIsValid = (permission: PermissionsConstants) => {
    let disabledPermissions = [PermissionsConstants.UseStructMaster];
    if (roleName !== SystemAdminRoleName) disabledPermissions.push(PermissionsConstants.ManageAdminOrganization);
    if (roleName !== PartnerAdminRoleName) disabledPermissions.push(PermissionsConstants.ManagePartnerOrganization);
    if (!hasGlobalLicensemanagerPermission) {
      disabledPermissions.push(PermissionsConstants.ApproveLicenses, PermissionsConstants.ManageLicenses, PermissionsConstants.ManageGlobalLicenses);
    }
    return !disabledPermissions.includes(permission);
  };

  const loadPermissions = async () => {
    if (roleItem == null && mode !== 'new') {
      if (roleId == null) navigate('not-found');
      roleItem = await roleStore.loadRole(roleId);
      setRoleName(roleItem.name);
      setRoleSelector(roleItem.selector);
      setPartnersOnly(roleItem.partnersOnly);
      setAddToScopeTypeCreator(roleItem.addToScopeTypeCreator);
      form.setFieldsValue({
        roleName: roleItem.name,
        selector: roleItem.selector,
        organization: roleItem.organization,
        partnersOnly: roleItem.partnersOnly,
        addToScopeTypeCreator: roleItem.addToScopeTypeCreator,
      });
    }

    if (mode !== 'new' && canSelectOrganization) {
      canSelectOrganization && setRoleOrg({ label: roleItem.organization, value: roleItem.organizationId });
    }

    const permissions: PermissionSummary[] = await permissionsStore.loadPermissions({
      orderBy: [
        { field: 'selector', direction: 'Descending', useProfile: false },
        { field: 'name', direction: 'Ascending', useProfile: false },
      ],
      skip: 0,
      take: 1000000,
      filter: [generateSelectorOdataFilter(Selector.Drillhole, maxSelector)],
    } as Query);
    setPermissionSelection(
      toObject<PermissionSummary, { permission: PermissionSummary; allowed: boolean }>(
        permissions,
        (p) => p.id,
        (p) => ({ permission: p, allowed: mode === 'new' ? false : roleItem.permissions.any((x: any) => x.permissionId === p.id) })
      )
    );
  };

  const goBack = () => {
    navigate(-1);
  };

  const reset = () => {
    roleStore.clearMessages();
    form.resetFields();
    let change = false;
    (getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection) || []).forEach(({ key, value }) => {
      if (mode === 'new') {
        if (value.allowed) {
          value.allowed = false;
          change = true;
        }
      } else {
        const old = value.allowed;
        value.allowed = roleItem.permissions.any((x: any) => x.permissionId === key);
        if (old !== value.allowed) change = true;
      }
    });

    if (change) setPermissionSelection({ ...permissionSelection });
    setRoleName(form.getFieldValue('roleName'));
    setRoleSelector(form.getFieldValue('selector'));
    setPartnersOnly(form.getFieldValue('partnersOnly'));
    setAddToScopeTypeCreator(form.getFieldValue('addToScopeTypeCreator'));
  };

  useEffect(() => {
    if (location.state == null && mode !== 'new') return;

    reset();
    loadPermissions();

    const mainContainer = document.querySelector('main.ant-layout-content');
    if (mainContainer) {
      mainContainer.classList.add('edit-mode');
    }
    return () => {
      mainContainer && mainContainer.classList.remove('edit-mode');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (location.state == null && mode !== 'new') {
    navigate(previousRoute);
    return null;
  }

  const onAllowPermissionChanged = (id: string, allowed: boolean) => {
    const item = permissionSelection[id];
    if (item == null || item.allowed === allowed) return;
    item.allowed = allowed;
    setPermissionSelection({ ...permissionSelection });
    if (allowed) {
      if (roleSelector == null) onRoleSelectorChanged(item.permission.selector);

      if (item.permission.selector === Selector.Partners && !partnersOnly && canBePartnersOnly) {
        onPartnersOnlyChanged(true);
      }
    }
  };

  const onRoleSelectorChanged = (nextSelector: Selector) => {
    setRoleSelector(nextSelector);
    form.setFieldsValue({ ...form.getFieldsValue(), selector: nextSelector });

    if (addToScopeTypeCreator && !isScopeTypeSelectorOk(nextSelector, addToScopeTypeCreator)) {
      setAddToScopeTypeCreator(undefined);
      form.setFieldsValue({ ...form.getFieldsValue(), addToScopeTypeCreator: undefined });
    }

    let change = false;
    const { minSelector: min, maxSelector: max } = getRolePermissionSelectors(nextSelector);
    (getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection) || []).forEach(({ value }) => {
      if (
        value.allowed &&
        (SelectorValue[value.permission.selector] < SelectorValue[min] || SelectorValue[value.permission.selector] > SelectorValue[max])
      ) {
        value.allowed = false;
        change = true;
      }
    });

    if (change) setPermissionSelection({ ...permissionSelection });
  };

  const onPartnersOnlyChanged = (value: boolean) => {
    form.setFieldsValue({ ...form.getFieldsValue(), partnersOnly: value });
    setPartnersOnly(value);
    if (value) return;

    let change = false;

    (getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection) || []).forEach(({ value }) => {
      if (value.allowed && value.permission.selector === Selector.Partners) {
        value.allowed = false;
        change = true;
      }
    });

    if (change) setPermissionSelection({ ...permissionSelection });
  };

  const roleIsDuplicated = async (roleName: string, organizationId?: string, organizationName?: string) => {
    let errorMessage = '';
    if (roleItem?.name === roleName) return false;

    if (isDefaultRole) {
      errorMessage = t('DuplicatedDefaultRoleNameErrorUpdate').replace('{0}', roleName);
    } else if (mode === 'edit') {
      errorMessage = t('DuplicatedRoleNameErrorUpdate')
        .replace('{0}', roleName)
        .replace('{1}', organizationName ?? '');
    } else {
      errorMessage = t('DuplicatedRoleNameErrorCreate')
        .replace('{0}', roleName)
        .replace('{1}', organizationName ?? '');
    }

    const filter = isDefaultRole ? { Name: roleName } : { Name: roleName, OrganizationId: guid(organizationId) };

    await roleSummaryStore.load({
      take: 1,
      filter,
    });

    if (roleSummaryState.items.value.length > 0) {
      notification.error({
        placement: 'bottomLeft',
        duration: 20,
        message: errorMessage,
      });
    }
    return roleSummaryState.items.value.length > 0;
  };

  const onSave = async () => {
    await form.validateFields();
    if (
      await roleIsDuplicated(
        curateString(roleName!),
        canSelectOrganization ? roleOrg?.value : currentOrg,
        canSelectOrganization ? roleOrg?.label : currentOrgName
      )
    )
      return;

    const newRole = {
      name: curateString(roleName!),
      organizationId: canSelectOrganization ? roleOrg!.value : currentOrg,
      selector: roleSelector!,
      partnersOnly: canBePartnersOnly ? partnersOnly : undefined,
      addToScopeTypeCreator: canBeAssignedByDefaultToScopeCreator ? addToScopeTypeCreator : undefined,
      permissions: (getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection) || [])
        .filter(({ value }) => value.allowed && SelectorValue[value.permission.selector] <= SelectorValue[roleSelector as Selector])
        .map((x) => x.key),
    };

    let response = await roleStore.update(roleId, newRole);
    if (response && response?.isSuccess) {
      goBack();
    } else {
      notification.error({
        placement: 'bottomLeft',
        duration: 20,
        message: t('ErrorUpdatingRole'),
      });
    }
  };

  const onCreate = async () => {
    await form.validateFields();
    if (
      await roleIsDuplicated(
        curateString(roleName!),
        canSelectOrganization ? roleOrg?.value : currentOrg,
        canSelectOrganization ? roleOrg?.label : currentOrgName
      )
    )
      return;

    const newRole = {
      name: curateString(roleName!),
      organizationId: canSelectOrganization ? roleOrg!.value : currentOrg,
      selector: roleSelector!,
      partnersOnly: partnersOnly,
      addToScopeTypeCreator: canBeAssignedByDefaultToScopeCreator ? addToScopeTypeCreator : undefined,
      permissions: (getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection) || [])
        .filter(({ value }) => value.allowed)
        .map((x) => x.key),
    };

    let response = await roleStore.create(newRole);
    if (response?.isSuccess) {
      goBack();
    }
  };

  const isSectionDisabled = (sectionSelector: Selector): boolean => {
    if (roleSelector == null) return false;
    const { minSelector, maxSelector } = getRolePermissionSelectors(roleSelector);
    return (
      SelectorValue[sectionSelector] < SelectorValue[minSelector] ||
      SelectorValue[sectionSelector] > SelectorValue[maxSelector] ||
      (sectionSelector === Selector.Partners && !partnersOnly && canBePartnersOnly === true)
    );
  };

  const onSelectorPermissionValuesToggled = (selector: Selector, toggleState: boolean) => {
    let change = false;
    (getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection) || []).forEach(({ value }) => {
      if (
        value.allowed === !toggleState &&
        SelectorValue[value.permission.selector] === SelectorValue[selector] &&
        permissionIsValid(value.permission.name as PermissionsConstants)
      ) {
        value.allowed = toggleState;
        change = true;
      }
    });

    if (toggleState && roleSelector == null && mode === 'new') {
      form.setFieldsValue({ ...form.getFieldsValue(), selector: selector });
      setRoleSelector(selector);
    }

    if (change) setPermissionSelection({ ...permissionSelection });
  };

  const onAllPermissionsValueToggled = (toggleState: boolean) => {
    let change = false;
    const currentSelector = roleSelector ?? maxSelector;
    (getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection) || []).forEach(({ value }) => {
      if (
        value.allowed === !toggleState &&
        SelectorValue[value.permission.selector] <= SelectorValue[currentSelector] &&
        permissionIsValid(value.permission.name as PermissionsConstants)
      ) {
        value.allowed = toggleState;
        change = true;
      }
    });

    if (toggleState && roleSelector == null && mode === 'new') {
      form.setFieldsValue({ ...form.getFieldsValue(), selector: currentSelector });
      setRoleSelector(currentSelector);
    }

    if (change) setPermissionSelection({ ...permissionSelection });
  };

  const isBusy = roleState.isBusy.get() || permissionsState.isBusy.get() || roleSummaryState.isBusy.get();

  return (
    <div className="entity-details_card-grid-container">
      {(roleState?.errorMessage?.value || '').length !== 0 && <Alert type="error" message={<>{roleState.errorMessage.value}</>} closable />}
      <Spin spinning={isBusy} size="large">
        <div className="entity-details_card-grid-container__form-container">
          <div
            className="entity-details_card-grid-container__left-container"
            style={{ width: '30%', minWidth: 200, borderRadius: 8, marginRight: 20 }}
          >
            <Form
              form={form}
              initialValues={
                mode === 'new'
                  ? undefined
                  : {
                      organization: roleItem?.organization,
                      roleName: roleItem?.name,
                      selector: roleItem?.selector,
                      partnersOnly: roleItem?.partnersOnly,
                      addToScopeTypeCreator: roleItem?.addToScopeTypeCreator,
                    }
              }
              layout="vertical"
              autoComplete="off"
              style={{ padding: 40, backgroundColor: bgColor }}
            >
              <Form.Item>
                <h3>{t('GeneralProperties')}</h3>
              </Form.Item>
              <Form.Item
                name={'roleName'}
                rules={[
                  { required: true, message: t('PleaseEnterName') },
                  () => ({
                    validator: (ruleObj: RuleObject, value: string) => {
                      const maxLenght = 128;
                      if (value === undefined) return Promise.resolve();
                      if (value.length > maxLenght) {
                        return Promise.reject(t('TextFieldLimitError').replace('{0}', t('RoleName')).replace('{1}', `${maxLenght}`));
                      } else return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input placeholder={t('RoleName')} autoComplete="off" onChange={(e) => setRoleName(e.target.value)} />
              </Form.Item>
              {canSelectOrganization && mode !== 'new' && (
                <Form.Item>
                  <Tag>{form.getFieldValue('organization')}</Tag>
                </Form.Item>
              )}
              {canSelectOrganization && mode === 'new' && (
                <Form.Item name="organization" rules={[{ required: true, message: t('PleaseSelectAnOrganization') }]}>
                  <OrganizationsSelect onChange={setRoleOrg} />
                </Form.Item>
              )}
              <Form.Item name="selector" rules={[{ required: true, message: t('PleaseSelectASelector') }]}>
                <SelectorSelect descending maxSelector={maxSelector} onChange={onRoleSelectorChanged} />
              </Form.Item>

              <Checkbox
                defaultChecked={false}
                style={{ marginBottom: 24 }}
                checked={getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection).every(
                  (x) =>
                    !permissionIsValid(x.value.permission.name as PermissionsConstants) ||
                    x.value.allowed ||
                    SelectorValue[x.value.permission.selector] > SelectorValue[roleSelector ?? maxSelector]
                )}
                onChange={(v) => onAllPermissionsValueToggled(v.target.checked)}
              >
                {t('AllowAllAvaliablePermissionsForThisSelector')}
              </Checkbox>

              {canBeAssignedByDefaultToScopeCreator && (
                <Form.Item name="addToScopeTypeCreator" rules={[{ required: false }]}>
                  <Select
                    options={getScopeTypeRangeBySelector(roleSelector).map((x) => ({ label: t(String(x)), value: x }))}
                    allowClear
                    onChange={setAddToScopeTypeCreator}
                    value={addToScopeTypeCreator}
                    placeholder={t('AssignByDefaultToScopeCreator')}
                  />
                </Form.Item>
              )}

              {canBePartnersOnly && (
                <Form.Item>
                  <Row align="middle">
                    <Form.Item name={'partnersOnly'} valuePropName="checked" style={{ margin: 0 }}>
                      <Switch onChange={onPartnersOnlyChanged} />
                    </Form.Item>
                    <h5 style={{ marginLeft: 10, marginBottom: 0 }}>{t('PartnersOnlyQuestion')}</h5>
                  </Row>
                </Form.Item>
              )}

              <Row gutter={[24, 16]}>
                <Col lg={12} xs={24}>
                  <Button disabled={isBusy} onClick={reset} className="big-modal-button primary-outline-button">
                    {t('Reset')}
                  </Button>
                </Col>
                <Col lg={12} xs={24}>
                  <Button disabled={isBusy} onClick={mode === 'new' ? onCreate : onSave} className="big-modal-button" type="primary">
                    {t('SaveChanges')}
                  </Button>
                </Col>
              </Row>
              {isDefaultRole && roleItem?.addToScopeTypeCreator != null && (
                <Row gutter={[24, 0]}>
                  <Alert style={{ marginTop: 10 }} type="warning" message={t('DefaultRoleWarning')} closable />
                </Row>
              )}
              {roleItem?.addToScopeTypeCreator != null && !isDefaultRole && (
                <Row gutter={[24, 16]}>
                  <Alert style={{ marginTop: 10 }} type="warning" message={t('RoleIsBeingAssignedToDefaultCreators')} closable />
                </Row>
              )}
            </Form>
          </div>
          <div
            className="entity-details_card-grid-container__right-container"
            style={{ width: '70%', minWidth: 200, borderRadius: 8, marginLeft: 20 }}
          >
            <Form style={{ padding: 40, backgroundColor: bgColor }}>
              <Form.Item>
                <h3>{t('RolesPermissions')}</h3>
              </Form.Item>
              {getKeyValuePairs<{ permission: PermissionSummary; allowed: boolean }>(permissionSelection)
                .map((x) => x.value)
                .groupBy<Selector>(
                  (x) => x.permission.selector,
                  (g1, g2) => SelectorValue[g2.key] - SelectorValue[g1.key]
                )
                .map((g) => ({ g: g, disabled: isSectionDisabled(g.key) }))
                .map(({ g, disabled }) => (
                  <div key={g.key} style={{ width: '70%' }}>
                    <div className="role-permissions_selector-group">
                      <Row style={disabled ? { opacity: 0.25 } : { opacity: 1 }} gutter={[8, 8]} align="middle">
                        <Col>
                          <strong>{t(g.key.toString())}</strong>
                        </Col>
                      </Row>

                      <Checkbox
                        defaultChecked={false}
                        disabled={disabled}
                        style={{ marginTop: 12, marginBottom: 12 }}
                        checked={g.items.every((v) => !permissionIsValid(v.permission.name as PermissionsConstants) || v.allowed)}
                        onChange={(v) =>
                          onSelectorPermissionValuesToggled(g.items.firstOrDefault()?.permission.selector ?? maxSelector, v.target.checked)
                        }
                      >
                        {t('AllowAllPermissionsForThisLevel')}
                      </Checkbox>

                      {g.items.map((v) => (
                        <Row
                          className={!permissionIsValid(v.permission.name as PermissionsConstants) ? 'hide-permission' : ''}
                          key={v.permission.id}
                          gutter={[24, 8]}
                          align="middle"
                          style={{ marginBottom: 16 }}
                        >
                          <Col lg={12} xs={24} style={disabled ? { opacity: 0.25 } : undefined}>
                            {t(v.permission.name)}
                          </Col>
                          <Col lg={12} xs={24}>
                            <Select
                              disabled={disabled}
                              style={{ width: '100%' }}
                              options={[
                                { label: t('Allow'), value: 1 },
                                { label: t('Deny'), value: 0 },
                              ]}
                              value={v.allowed ? 1 : 0}
                              onChange={(val) => onAllowPermissionChanged(v.permission.id, val === 1)}
                            />
                          </Col>
                        </Row>
                      ))}
                    </div>
                    <Divider />
                  </div>
                ))}
            </Form>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default RoleInformationPage;
